import { useCallback, useEffect, useState } from 'react';
import { api, ApiFailure } from '../../api';
import { Notice } from '../Panels';
import { Field, Hint } from '../Hint';
import { useToast } from '../Toast';
import { useConfirm } from '../Confirm';

interface PortalSummary {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  reportCount: number;
  dashboardCount: number;
  createdAt: string;
}

/**
 * The workspace's client portals: branded pages a client opens to see their
 * reports. Create one per client, pause it when the engagement rests, delete it
 * when it ends. (Granting a client access to a portal still happens from the
 * Client Portals page, where the access links are generated.)
 */
export function Portals() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [portals, setPortals] = useState<PortalSummary[] | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const reload = useCallback(() => {
    api
      .listPortals()
      .then((response) => {
        setPortals(response.portals);
        setFailure(null);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiFailure) setFailure(error);
        setPortals([]);
      });
  }, []);

  useEffect(reload, [reload]);

  async function toggleActive(portal: PortalSummary): Promise<void> {
    try {
      await api.updatePortal(portal.id, { active: !portal.active });
      toast.success(
        portal.active ? `"${portal.name}" is paused — clients can no longer open it.` : `"${portal.name}" is live again.`,
      );
      reload();
    } catch (error) {
      toast.error(error instanceof ApiFailure ? error.message : 'The portal could not be updated.');
    }
  }

  async function saveRename(portal: PortalSummary): Promise<void> {
    const name = renameValue.trim();
    if (name === '' || name === portal.name) {
      setRenaming(null);
      return;
    }
    try {
      await api.updatePortal(portal.id, { name });
      toast.success('Portal renamed.');
      setRenaming(null);
      reload();
    } catch (error) {
      toast.error(error instanceof ApiFailure ? error.message : 'The portal could not be renamed.');
    }
  }

  async function remove(portal: PortalSummary): Promise<void> {
    const ok = await confirm({
      title: `Delete "${portal.name}"?`,
      body: 'Clients with a link to this portal will immediately be unable to open it. This cannot be undone.',
      confirmLabel: 'Delete portal',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deletePortal(portal.id);
      toast.success(`"${portal.name}" has been deleted.`);
      reload();
    } catch (error) {
      toast.error(error instanceof ApiFailure ? error.message : 'The portal could not be deleted.');
    }
  }

  if (portals === null) {
    return (
      <>
        {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}
        <p className="muted">
          <span className="spinner" /> Loading…
        </p>
      </>
    );
  }

  return (
    <>
      <header className="intro">
        <h2>
          Client portals
          <Hint text="A portal is a branded page your client opens to see their reports — your name on it, not ours. Each has its own private link." />
        </h2>
        <p>
          {portals.length === 0
            ? 'Give each client a branded page with their reports on it.'
            : `${portals.length} ${portals.length === 1 ? 'portal' : 'portals'} — share links for a portal are made from the Client Portals page in the sidebar.`}
        </p>
        <button className="primary" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Cancel' : 'New portal'}
        </button>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {showCreate && (
        <CreatePortalForm
          onCreated={() => {
            setShowCreate(false);
            toast.success('Portal created. Open Client Portals in the sidebar to share it with a client.');
            reload();
          }}
        />
      )}

      {portals.length === 0 && !showCreate ? (
        <div className="card empty">
          <h3>No portals yet</h3>
          <p>Create a branded portal for your first client. They get a private link with your name on the page.</p>
        </div>
      ) : (
        <section className="card">
          <header>
            <h2>Your portals</h2>
            <span className="sub">{portals.length}</span>
          </header>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th className="num">Reports</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {portals.map((portal) => (
                  <tr key={portal.id}>
                    <td>
                      {renaming === portal.id ? (
                        <span className="row tight">
                          <input
                            type="text"
                            value={renameValue}
                            autoFocus
                            onChange={(event) => setRenameValue(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') void saveRename(portal);
                              if (event.key === 'Escape') setRenaming(null);
                            }}
                          />
                          <button className="tiny" onClick={() => void saveRename(portal)}>
                            Save
                          </button>
                          <button className="ghost tiny" onClick={() => setRenaming(null)}>
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <>
                          <strong>{portal.name}</strong>
                          <br />
                          <span className="muted" style={{ fontSize: 12 }}>
                            /portal/{portal.slug}
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${portal.active ? 'good' : ''}`}>
                        {portal.active ? 'active' : 'paused'}
                      </span>
                    </td>
                    <td className="num">{portal.reportCount}</td>
                    <td>{new Date(portal.createdAt).toLocaleDateString()}</td>
                    <td>
                      {renaming !== portal.id && (
                        <div className="row tight">
                          <button
                            className="ghost tiny"
                            onClick={() => {
                              setRenaming(portal.id);
                              setRenameValue(portal.name);
                            }}
                          >
                            Rename
                          </button>
                          <button className="ghost tiny" onClick={() => void toggleActive(portal)}>
                            {portal.active ? 'Pause' : 'Resume'}
                          </button>
                          <button className="ghost tiny danger" onClick={() => void remove(portal)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function CreatePortalForm({ onCreated }: { onCreated: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [busy, setBusy] = useState(false);

  async function create(): Promise<void> {
    setBusy(true);
    try {
      await api.createPortal({
        name: name.trim(),
        branding: { companyName: companyName.trim() === '' ? undefined : companyName.trim() },
      });
      onCreated();
    } catch (error) {
      toast.error(error instanceof ApiFailure ? error.message : 'The portal could not be created.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <header>
        <h2>New portal</h2>
        <span className="sub">a branded page for one client</span>
      </header>
      <div className="row" style={{ alignItems: 'flex-end', marginTop: 4 }}>
        <Field
          label="Portal name"
          hint="Only you see this — it names the portal in your list, e.g. “Acme Corp”."
          className="grow"
        >
          <input
            type="text"
            value={name}
            placeholder="Acme Corp"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field
          label="Your company name (shown to the client)"
          hint="Printed on the portal page so the client sees your brand, not DataVantage."
          className="grow"
        >
          <input
            type="text"
            value={companyName}
            placeholder="Your Agency Name"
            onChange={(event) => setCompanyName(event.target.value)}
          />
        </Field>
        <button className="primary" disabled={busy || name.trim() === ''} onClick={() => void create()}>
          {busy ? <span className="spinner" /> : 'Create portal'}
        </button>
      </div>
    </section>
  );
}
